import {getCurrentScope, nextTick, onScopeDispose, ref, watch, type Ref} from 'vue';

/** Long enough for a smooth scroll of a screen or two to come to rest. */
const SCROLL_SETTLE_MS = 450;

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function sameFace(one: ExploreTileFace, other: ExploreTileFace): boolean {
  return one.firstUse === other.firstUse && one.hasNewContent === other.hasNewContent;
}

/** Whether the whole of it is on screen, which is the only case that needs no scroll at all. */
function isFullyVisible(element: Element): boolean {
  const box = element.getBoundingClientRect();

  return box.top >= 0 && box.bottom <= globalThis.innerHeight;
}

async function takeReaderToTile(): Promise<void> {
  const tile = document.querySelector('[data-tile="explore"]');
  if (!tile || isFullyVisible(tile)) {
    return;
  }

  tile.scrollIntoView({behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center'});
  if (!prefersReducedMotion()) {
    await new Promise((resolve) => {
      setTimeout(resolve, SCROLL_SETTLE_MS);
    });
  }
}

/**
 * Changes the page, morphing the bento between the two layouts where the browser can do it. The
 * row's shape is a grid span, which nothing can transition; the row carries a view-transition name
 * so the browser replaces it with its new arrangement instead.
 *
 * Only for a change the reader watches happen. A view transition takes the whole document out of
 * hit testing for as long as it runs, which is a fair price for the change they just caused and no
 * price worth paying on a page that is still arriving.
 */
function changeShape(apply: () => void): void {
  if (!('startViewTransition' in document)) {
    apply();
    return;
  }
  document.startViewTransition(async() => {
    apply();
    // The snapshot of the new layout is taken when this resolves, so it has to outlast Vue's own
    // update rather than the assignment that schedules it.
    await nextTick();
  });
}

interface ExploreRingReveal {
  /** The face to draw, which is the payload's unless a change is being held back. */
  face: Ref<ExploreTileFace>;
  /**
   * The reader has just answered an assessment, so the change about to happen is one they caused
   * and should see: the next one waits for the ring's pictures, takes them to the tile and morphs.
   *
   * Takes the load it is waiting on, so that a payload arriving with nothing changed about the tile
   * — the platform had already chosen its content again, say — puts the expectation down instead of
   * leaving it for a change nobody caused.
   */
  expectAfterAnswer(landed: Promise<unknown>): void;
}

/**
 * When the Explore tile changes face, and the bento its shape with it (IPD-3256).
 *
 * A page that arrives in a state draws it: the payload knows the shape and the face at the first
 * render, and only the ring inside the card is still on its way.
 *
 * A change the reader causes is held until the pictures are in the browser. The row reflows and a
 * cover drops into the ring as it does, and a picture that changes after that reads as the card
 * having lost its place. The assessment tile sits above this one, so the card that answers them
 * can be off screen entirely: they are taken to it first, and it changes while they are looking.
 */
export function useExploreRingReveal(
  state: () => ExploreTileFace,
  coversSettled: () => boolean,
): ExploreRingReveal {
  const face = ref<ExploreTileFace>({firstUse: false, hasNewContent: false});
  // The reader is taken to the tile before the card changes, and that wait outlives a page they
  // navigate away from: without this the view transition runs over whatever they went to.
  let onThisPage = true;
  if (getCurrentScope()) {
    onScopeDispose(() => {
      onThisPage = false;
    });
  }
  // The face the answer was given from. Held rather than a flag, because the pictures settle again
  // as part of answering — the cover that drops in is read then — and a flag would be spent on
  // that, before the payload the reader is actually waiting for has said anything.
  let answeredFrom: ExploreTileFace | null = null;

  watch([state, coversSettled], ([current, settled]) => {
    if (!answeredFrom) {
      face.value = current;
      return;
    }
    // Their answer has not come back yet, whatever else has moved in the meantime.
    if (sameFace(current, answeredFrom)) {
      return;
    }
    // A state with no ring has no picture to wait for and nothing to be taken to: the card is
    // giving the row back, which is the one change that only ever shrinks what is on screen.
    const wantsRing = current.firstUse || current.hasNewContent;
    if (!wantsRing) {
      answeredFrom = null;
      changeShape(() => {
        face.value = current;
      });
      return;
    }
    if (!settled) {
      return;
    }
    answeredFrom = null;
    void takeReaderToTile().then(() => {
      if (!onThisPage) {
        return;
      }
      changeShape(() => {
        face.value = current;
      });
    });
  }, {immediate: true});

  return {
    face,
    expectAfterAnswer: (landed: Promise<unknown>): void => {
      const from = {...face.value};
      answeredFrom = from;
      void landed.then(() => {
        if (answeredFrom === from && sameFace(state(), from)) {
          answeredFrom = null;
        }
      });
    },
  };
}

/** Which of the tile's two faces is on the card, and with it whether it takes the discover row. */
export interface ExploreTileFace {
  firstUse: boolean;
  hasNewContent: boolean;
}
